import { Box, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../../api/hooks/useColors";
import { controlKeys } from "../../../../../../../api/queryKeys";
import IconButton from "../../../../../../../components/buttons/IconButton";
import CustomTooltip from "../../../../../../../components/CustomTooltip";
import { Toast } from "../../../../../../../components/Toast";
import { icons } from "../../../../../../stack/environment/icons/iconExport";

export default function AddCommentBox({ controlID }) {
  const MAX_COMMENT_LENGTH = 350;
  const colors = useColors();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");
  const { addCommentToControlActivityLogs, trackEvent } = useAPI();
  const [isFocused, setIsFocused] = useState(false);

  const commentMutation = useMutation({
    mutationFn: () => addCommentToControlActivityLogs(controlID, comment),
    onSuccess: () => {
      setComment("");
      queryClient.invalidateQueries({
        queryKey: controlKeys.controlActivityLogs(controlID),
      });
    },
    onError: () => {
      Toast.error("Failed to comment");
    },
  });

  const handleComment = () => {
    trackEvent(`User commented on control ${controlID}`);
    commentMutation.mutate();
  };

  return (
    <Box paddingY="12px">
      <Box
        display="flex"
        position="relative"
        sx={{
          // on focus 1px solid " + colors.textfield_border_focus
          border: `1px solid ${
            !isFocused ? colors.enabled_stroke_20 : colors.grey_300
          }`,
          borderRadius: "8px",
          backgroundColor: colors.common_white,
          padding: "0 0 6px 0",
        }}
      >
        <Box width="95%">
          <TextField
            placeholder="Leave a comment..."
            fullWidth
            multiline
            value={comment}
            onChange={(e) => {
              if (e.target.value.length <= MAX_COMMENT_LENGTH) {
                setComment(e.target.value);
              }
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            minRows={2}
            maxRows={2}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                (e.metaKey || e.ctrlKey) &&
                !e.shiftKey &&
                !e.altKey
              ) {
                e.preventDefault();
                handleComment();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                padding: "8px 12px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "0",
                lineHeight: "24px",
                color: colors.text_primary,
                scrollbarWidth: "none",
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
              },
              "& .MuiOutlinedInput-input::placeholder": {
                color: colors.text_help,
                opacity: 1,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
          />
        </Box>
        <CustomTooltip title="Submit comment" placement="bottom">
          <Box position="absolute" right="11px" bottom="9px">
            <IconButton
              onClick={handleComment}
              IconSVG={icons.ArrowUpIcon}
              size="XS"
              disabled={!comment}
              loading={commentMutation.isPending}
            />
          </Box>
        </CustomTooltip>
      </Box>
    </Box>
  );
}
